import React from 'react';
import { DesiredActionKey } from '@dreamai/types';
import { getActionKeyLabel } from '../../lib/desiredActionFields';
import { ReactComponent as ActionBuyIcon } from '../../icons/action-buy.svg';
import { ReactComponent as ActionResearchIcon } from '../../icons/action-research.svg';
import { ReactComponent as ActionEmailSendIcon } from '../../icons/action-email-send.svg';
import { ReactComponent as ActionDraftEmailIcon } from '../../icons/action-draft-email.svg';
import { ReactComponent as ActionBuyAndActIcon } from '../../icons/action-buy-and-act.svg';
import { ReactComponent as ActionCalendarInviteIcon } from '../../icons/action-calendar-invite.svg';
import { ReactComponent as ActionBringAndBuyIcon } from '../../icons/action-bring-and-buy.svg';
import { ReactComponent as ActionPhoneIcon } from '../../icons/action-phone.svg';
import { ReactComponent as ActionPresentOptionsIcon } from '../../icons/action-present-options.svg';
import { ReactComponent as ActionFillInIcon } from '../../icons/action-fill-in.svg';
import { ReactComponent as ActionOtherIcon } from '../../icons/action-other.svg';
import { ReactComponent as ActionProjectIcon } from '../../icons/action-project.svg';
import { ReactComponent as TransportItineraryIcon } from '../../icons/transport-itinerary.svg';
import { ReactComponent as SwitchArrowsIcon } from '../../icons/switch-arrows.svg';
import { ReactComponent as MagicWandIcon } from '../../icons/magic-wand.svg';
import { ReactComponent as CloseXIcon } from '../../icons/close-x.svg';
import { ReactComponent as CheckmarkIcon } from '../../icons/checkmark.svg';
import { ReactComponent as ActionCreateIcon } from '../../icons/action-create.svg';

const ACTION_ICONS: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  buy: ActionBuyIcon,
  reserve: ActionBuyIcon,
  research: ActionResearchIcon,
  present_options: ActionPresentOptionsIcon,
  email_from_assistant: ActionEmailSendIcon,
  draft_email_from_user: ActionDraftEmailIcon,
  phone_from_assistant: ActionPhoneIcon,
  fill_in_by_assistant: ActionFillInIcon,
  send_calendar_invite: ActionCalendarInviteIcon,
  bring: TransportItineraryIcon,
  bring_and_buy: ActionBringAndBuyIcon,
  act: SwitchArrowsIcon,
  buy_and_act: ActionBuyAndActIcon,
  project: ActionProjectIcon,
  autopilot: MagicWandIcon,
  other: ActionOtherIcon,
  create: ActionCreateIcon,
};

export interface VoiceCallTodoCardProps {
  id: string;
  description: string;
  actionKey: string;
  actionLabel: string;
  status: 'pending' | 'creating' | 'created';
  spendDescription?: string;
  estimatedSpend?: string;
  spendCurrency?: string;
  agentNickname?: string;
  onCreateTodo: (id: string) => void;
  onRemove: (id: string) => void;
}

const VoiceCallTodoCard: React.FC<VoiceCallTodoCardProps> = ({
  id,
  description,
  actionKey,
  actionLabel,
  status,
  spendDescription,
  estimatedSpend,
  spendCurrency,
  agentNickname,
  onCreateTodo,
  onRemove,
}) => {
  const Icon = ACTION_ICONS[actionKey] || ActionOtherIcon;
  const isCreated = status === 'created';
  const isCreating = status === 'creating';

  return (
    <div className={`voice-call-todo-card${isCreated ? ' created' : ''}`}>
      {/* Remove button */}
      {!isCreated && (
        <button
          className='voice-todo-remove-btn'
          onClick={() => onRemove(id)}
          title='Remove'
        >
          <CloseXIcon />
        </button>
      )}

      {/* Todo description */}
      <div className='voice-todo-description'>
        <span className='voice-todo-text'>{description}</span>
      </div>

      {/* Spend badge */}
      {estimatedSpend && spendCurrency && (
        <div className='voice-todo-spend'>
          <span className='voice-todo-spend-label'>
            Up to{' '}
            {spendCurrency === 'GBP'
              ? '£'
              : spendCurrency === 'USD'
                ? '$'
                : spendCurrency === 'EUR'
                  ? '€'
                  : `${spendCurrency} `}
            {estimatedSpend}
            {spendDescription ? ` on ${spendDescription}` : ''}
          </span>
        </div>
      )}

      {/* Action + buttons row */}
      <div className='voice-todo-footer'>
        <div className='voice-todo-action'>
          <Icon className='voice-todo-action-icon' />
          <span className='voice-todo-action-label'>
            {actionLabel ||
              getActionKeyLabel(actionKey as DesiredActionKey, agentNickname)}
          </span>
        </div>

        <div className='voice-todo-actions'>
          {!isCreated && (
            <button
              className='voice-todo-create-btn'
              onClick={() => onCreateTodo(id)}
              disabled={isCreating}
            >
              {isCreating ? 'Creating...' : 'Create Todo'}
            </button>
          )}
          {isCreated && (
            <span className='voice-todo-created-badge'>
              <CheckmarkIcon /> Created
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoiceCallTodoCard;
